import { A } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import {
  createPlaygroundDrafts,
  createPromptBundles,
  invalidateRuns,
} from "./playgroundQueries";
import { VoiceDrawer } from "./VoiceDrawer";

/** The experiment controls: pick the AI instructions (prompt) and scoring guide
 * (catalog), then launch a run. Both default to what production uses. */
export function RunParamsBar(props: {
  websiteId: string;
  clientId: string;
  clientName: string;
  hasVoice: boolean;
}) {
  const bundles = createPromptBundles();
  const drafts = createPlaygroundDrafts();

  // "" = the surface's active prompt / the canonical catalog (production).
  const [promptId, setPromptId] = createSignal("");
  const [draftId, setDraftId] = createSignal("");
  const action = createAsyncAction();
  const [voiceOpen, setVoiceOpen] = createSignal(false);

  const run = () => {
    if (action.busy()) return;
    void action.run(async () => {
      await api.playground.createRun(
        props.websiteId,
        draftId() || null,
        promptId() || null,
      );
      await invalidateRuns(props.websiteId);
    });
  };

  return (
    <div class="param-bar">
      <label class="field">
        <span class="field-label">AI instructions (prompt)</span>
        <select
          class="editor-input"
          value={promptId()}
          onChange={(e) => setPromptId(e.currentTarget.value)}
        >
          <option value="">Active prompt (default)</option>
          <For each={bundles.data?.bundles ?? []}>
            {(bundle) => (
              <option value={bundle.id}>
                {bundle.label}
                {bundle.isActive ? " ★" : ""}
              </option>
            )}
          </For>
        </select>
        <span class="field-hint">
          How the agent is told to inspect the page.
        </span>
      </label>

      <label class="field">
        <span class="field-label">Scoring guide (catalog)</span>
        <select
          class="editor-input"
          value={draftId()}
          onChange={(e) => setDraftId(e.currentTarget.value)}
        >
          <option value="">Canonical guide (default)</option>
          <For each={drafts.data?.drafts ?? []}>
            {(draft) => <option value={draft.id}>{draft.label}</option>}
          </For>
        </select>
        <span class="field-hint">
          Which metrics and checks the run is scored against.
        </span>
      </label>

      <div class="field" style={{ flex: "0 1 auto" }}>
        <span class="field-label">Brand voice</span>
        <span class="badge">{props.hasVoice ? "active" : "none"}</span>
        <span class="param-links">
          <button
            type="button"
            class="link-button"
            onClick={() => setVoiceOpen(true)}
          >
            {props.hasVoice ? "Edit voice" : "Add voice"}
          </button>
          <A href="/playground/prompts">Edit prompts</A>
          <A
            href={`/playground/drafts/new?from=/playground/w/${props.websiteId}`}
          >
            New guide
          </A>
        </span>
      </div>

      <div class="param-bar-actions">
        <button
          class="btn btn-primary"
          type="button"
          {...adminGateProps(action.busy(), "run the agent")}
          onClick={run}
        >
          {action.busy() ? "Starting…" : "Run the agent ▶"}
        </button>
      </div>
      <Show when={action.error()}>
        <div class="gate-error" style={{ flex: "1 1 100%" }}>
          {action.error()}
        </div>
      </Show>
      <Show when={voiceOpen()}>
        <VoiceDrawer
          clientId={props.clientId}
          clientName={props.clientName}
          onClose={() => setVoiceOpen(false)}
        />
      </Show>
    </div>
  );
}
